import { useState, useEffect } from 'react'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import type { OrganizationDomain } from '../../types/database'
import {
  VStack,
  HStack,
  Card,
  PageHeader,
  Text,
  Button,
  IconButton,
  Input,
  Badge,
  Table,
  Banner,
  ConfirmDialog,
  Animate,
  Modal,
  CopyField
} from 'wss3-forge'
import {
  Add20Regular,
  Delete20Regular,
  ArrowSync20Regular
} from '@fluentui/react-icons'

export function OrgDomains() {
  const { organization } = useOrg()

  const [domains, setDomains] = useState<OrganizationDomain[]>([])
  const [loading, setLoading] = useState(true)
  const [newDomain, setNewDomain] = useState('')
  const [addingDomain, setAddingDomain] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState<OrganizationDomain | null>(null)
  const [showVerifyModal, setShowVerifyModal] = useState(false)
  const [verificationToken, setVerificationToken] = useState('')
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    loadDomains()
  }, [organization.id])

  const loadDomains = async () => {
    try {
      const { data, error } = await supabase.rpc('get_org_domains', {
        p_organization_id: organization.id
      })

      if (error) throw error
      setDomains(data || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return

    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}$/
    if (!domainRegex.test(newDomain.trim())) {
      setError('Invalid domain format (e.g., example.com)')
      return
    }

    setAddingDomain(true)
    setError(null)

    try {
      const { data, error } = await supabase
        .from('organization_domains')
        .insert({
          organization_id: organization.id,
          domain: newDomain.trim().toLowerCase()
        })
        .select()
        .single()

      if (error) {
        if (error.code === '23505') {
          setError('This domain is already configured')
        } else {
          throw error
        }
        return
      }

      setDomains(prev => [...prev, data])
      setNewDomain('')

      setSelectedDomain(data)
      await loadVerificationToken(data.id)
      setShowVerifyModal(true)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setAddingDomain(false)
    }
  }

  const loadVerificationToken = async (domainId: string) => {
    try {
      const { data, error } = await supabase.rpc('generate_domain_verification_token', {
        p_domain_id: domainId
      })

      if (error) throw error
      setVerificationToken(data)
    } catch {
      // Token generation failed silently
    }
  }

  const handleVerifyDomain = async () => {
    if (!selectedDomain) return

    setVerifying(true)

    try {
      const { error } = await supabase
        .from('organization_domains')
        .update({
          verified: true,
          verified_at: new Date().toISOString()
        })
        .eq('id', selectedDomain.id)

      if (error) throw error

      setDomains(prev => prev.map(d =>
        d.id === selectedDomain.id
          ? { ...d, verified: true, verified_at: new Date().toISOString() }
          : d
      ))

      setShowVerifyModal(false)
      setSelectedDomain(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setVerifying(false)
    }
  }

  const handleDeleteDomain = async () => {
    if (!selectedDomain) return

    try {
      const { error } = await supabase
        .from('organization_domains')
        .delete()
        .eq('id', selectedDomain.id)

      if (error) throw error

      setDomains(prev => prev.filter(d => d.id !== selectedDomain.id))
      setShowDeleteConfirm(false)
      setSelectedDomain(null)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const openVerifyModal = async (domain: OrganizationDomain) => {
    setSelectedDomain(domain)
    await loadVerificationToken(domain.id)
    setShowVerifyModal(true)
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <PageHeader
          title="Email Domains"
          subtitle="Users with these email domains will be redirected to your organization's SSO."
        />
      </Animate>

      {error && (
        <Banner variant="error" title="Error" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Animate type="slideInUp" delay={50}>
        <Card padding="lg" title="Add a Domain">
          <HStack gap="sm">
            <div style={{ flex: 1 }}>
              <Input
                placeholder="example.com"
                value={newDomain}
                onChange={(v) => setNewDomain(v as string)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
              />
            </div>
            <Button
              icon={<Add20Regular />}
              onClick={handleAddDomain}
              loading={addingDomain}
              disabled={!newDomain.trim()}
            >
              Add
            </Button>
          </HStack>
        </Card>
      </Animate>

      <Animate type="slideInUp" delay={100}>
        <Table
          title="Configured Domains"
          columns={[
            {
              key: 'domain',
              header: 'Domain',
              render: (_, row: OrganizationDomain) => (
                <Text weight="medium">@{row.domain}</Text>
              )
            },
            {
              key: 'status',
              header: 'Status',
              render: (_, row: OrganizationDomain) => (
                <Badge variant={row.verified ? 'success' : 'warning'}>
                  {row.verified ? 'Verified' : 'Unverified'}
                </Badge>
              )
            },
            {
              key: 'verified_at',
              header: 'Verified on',
              render: (_, row: OrganizationDomain) => (
                <Text size="sm" color="muted">
                  {row.verified_at
                    ? new Date(row.verified_at).toLocaleDateString('en-US')
                    : '-'}
                </Text>
              )
            },
            {
              key: 'actions',
              header: '',
              render: (_, row: OrganizationDomain) => (
                <HStack gap="xs">
                  {!row.verified && (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<ArrowSync20Regular />}
                      onClick={() => openVerifyModal(row)}
                    >
                      Verify
                    </Button>
                  )}
                  <IconButton
                    icon={<Delete20Regular />}
                    size="sm"
                    onClick={() => {
                      setSelectedDomain(row)
                      setShowDeleteConfirm(true)
                    }}
                  />
                </HStack>
              )
            }
          ]}
          data={domains}
          loading={loading}
        />
      </Animate>

      <Animate type="slideInUp" delay={150}>
        <Banner variant="info" title="How to verify a domain?">
          <VStack gap="sm">
            <Text size="sm">
              To prove that you own the domain, add a DNS TXT record with the provided verification token.
            </Text>
            <Text size="sm" color="muted">
              Verification may take up to 48 hours due to DNS propagation.
            </Text>
          </VStack>
        </Banner>
      </Animate>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Domain"
        description={`Are you sure you want to delete the domain "${selectedDomain?.domain}"? Users from this domain will no longer be able to use SSO.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteDomain}
      />

      <Modal
        open={showVerifyModal}
        onClose={() => {
          setShowVerifyModal(false)
          setSelectedDomain(null)
        }}
        title="Verify Domain"
      >
        <VStack gap="md">
          <Text>
            To verify that you own <strong>{selectedDomain?.domain}</strong>, add the following DNS record:
          </Text>

          <Card variant="outlined" padding="md">
            <VStack gap="md">
              <div>
                <Text size="sm" color="muted">Type</Text>
                <Text weight="medium">TXT</Text>
              </div>
              <CopyField
                label="Name / Host"
                value={`_webba-verification.${selectedDomain?.domain}`}
              />
              <CopyField
                label="Value"
                value={verificationToken}
              />
            </VStack>
          </Card>

          <Banner variant="warning" title="Note">
            DNS propagation can take up to 48 hours. Once the record is added, click "Mark as Verified".
          </Banner>

          <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <Button variant="secondary" onClick={() => setShowVerifyModal(false)}>
              Close
            </Button>
            <Button onClick={handleVerifyDomain} loading={verifying}>
              Mark as Verified
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </VStack>
  )
}
